import { Component } from '@theme/component';

/**
 * An animated disclosure panel using inert + max-height animation.
 *
 * Works with any element following the details/summary pattern — the content need not live in a
 * `<details>`. Uses measured content height rather than modern alternatives for older iOS Safari.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger
 * @property {HTMLElement} disclosureContent
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];
  #contentObserver = null;
  #heightSyncQueued = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observeContentMutations();
    this.syncContentHeight();
  }

  disconnectedCallback() {
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with measured max-height animations for older Safari compatibility.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );

    this.syncContentHeight();

    // Toggle the inert attribute directly so CSS selectors stay in sync.
    if (expanded) {
      content.setAttribute('inert', '');
    } else {
      content.removeAttribute('inert');
    }
  };

  /** Syncs the max-height CSS variable with current content height. */
  syncContentHeight = () => {
    const { disclosureContent: content } = this.refs;
    content.style.setProperty('--disclosure-content-max-height', `${content.scrollHeight}px`);
  };

  #scheduleHeightSync = () => {
    if (this.#heightSyncQueued) return;

    this.#heightSyncQueued = true;
    requestAnimationFrame(() => {
      this.#heightSyncQueued = false;
      this.syncContentHeight();
    });
  };

  #observeContentMutations = () => {
    const { disclosureContent: content } = this.refs;
    this.#contentObserver?.disconnect();
    this.#contentObserver = new MutationObserver(() => {
      this.#scheduleHeightSync();
    });

    this.#contentObserver.observe(content, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden'],
    });
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
